import { useCallback, useMemo, useState } from 'react'
import { useNls } from '../../nls/nlsProvider'

function isObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (!isObject(a) || !isObject(b)) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false
    }
    return true
  }

  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false
    if (!deepEqual(a[key], b[key])) return false
  }

  return true
}

export type CommitResult =
  | { ok: true }
  | { ok: false; error: string }

export interface UseSettingsDraftOptions<T> {
  initialValue: T
  onSave?: (draft: T) => Promise<void>
}

export interface UseSettingsDraftResult<T> {
  draft: T
  set: (updater: T | ((prev: T) => T)) => void
  setField: <K extends keyof T>(key: K, value: T[K]) => void
  dirty: boolean
  saving: boolean
  error: string | null
  reset: () => void
  commit: (customSave?: (draft: T) => Promise<void>) => Promise<CommitResult>
}

/**
 * useSettingsDraft (SR5)
 * Draft state manager for settings that require explicit save (numbers, text inputs, sliders).
 * Tracks dirty state via deep equality, drives SettingsSaveBar.
 */
export function useSettingsDraft<T>({
  initialValue,
  onSave,
}: UseSettingsDraftOptions<T>): UseSettingsDraftResult<T> {
  const { t } = useNls()
  const [committed, setCommitted] = useState<T>(initialValue)
  const [draft, setDraft] = useState<T>(initialValue)
  const [prevInitial, setPrevInitial] = useState<T>(initialValue)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const dirty = useMemo(() => !deepEqual(draft, committed), [draft, committed])

  // When initialValue changes deep-unequal and the draft is not dirty, re-base both committed and draft
  if (!deepEqual(initialValue, prevInitial)) {
    setPrevInitial(initialValue)
    if (!dirty) {
      setCommitted(initialValue)
      setDraft(initialValue)
    }
  }

  const set = useCallback((updater: T | ((prev: T) => T)) => {
    setDraft((prev) =>
      typeof updater === 'function'
        ? (updater as (prev: T) => T)(prev)
        : updater,
    )
    setError(null)
  }, [])

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraft((prev) => {
      if (isObject(prev)) {
        return { ...prev, [key]: value }
      }
      return prev
    })
    setError(null)
  }, [])

  const reset = useCallback(() => {
    setDraft(committed)
    setError(null)
  }, [committed])

  const commit = useCallback(
    async (customSave?: (draft: T) => Promise<void>): Promise<CommitResult> => {
      setSaving(true)
      setError(null)
      try {
        const saveAction = customSave ?? onSave
        if (saveAction) {
          await saveAction(draft)
        }
        setCommitted(draft)
        setSaving(false)
        return { ok: true }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t('NLSSettingsKitSaveFailed')
        setError(message)
        setSaving(false)
        return { ok: false, error: message }
      }
    },
    [draft, onSave, t],
  )

  return {
    draft,
    set,
    setField,
    dirty,
    saving,
    error,
    reset,
    commit,
  }
}
